import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { CheckSquare, Plus, Loader2, Circle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    category: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (error) {
      toast.error('Error fetching tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/tasks`, form, { withCredentials: true });
      toast.success('Task added!');
      setDialogOpen(false);
      setForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', category: '' });
      fetchTasks();
    } catch (error) {
      toast.error('Error adding task');
    }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${taskId}`, { completed: !completed }, { withCredentials: true });
      fetchTasks();
    } catch (error) {
      toast.error('Error updating task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const incompleteTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground mt-1">Manage your wedding tasks</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-task-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Task</DialogTitle>
              <DialogDescription>Create a new task for your wedding</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Task Title</Label>
                <Input
                  placeholder="e.g., Book photographer"
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Task details..."
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(val) => setForm({...form, priority: val})}>
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({...form, due_date: e.target.value})}
                    data-testid="task-due-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Input
                  placeholder="Person responsible"
                  value={form.assigned_to}
                  onChange={(e) => setForm({...form, assigned_to: e.target.value})}
                  data-testid="task-assigned-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  placeholder="e.g., Vendor, Venue, Decor"
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  data-testid="task-category-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-task-button">
                Add Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {tasks.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No tasks yet. Add your first task!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {incompleteTasks.length > 0 && (
            <div>
              <h2 className="text-lg font-medium mb-4">Pending Tasks ({incompleteTasks.length})</h2>
              <div className="space-y-3">
                {incompleteTasks.map((task) => (
                  <Card key={task.id} className="shadow-sm hover:shadow-md transition-shadow" data-testid="task-card">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => toggleTask(task.id, task.completed)}
                          className="mt-1"
                          data-testid="task-checkbox"
                        >
                          <Circle className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium">{task.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              task.priority === 'high' ? 'bg-destructive/10 text-destructive' :
                              task.priority === 'medium' ? 'bg-secondary/10 text-secondary-foreground' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {task.priority}
                            </span>
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                          )}
                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                            {task.assigned_to && <span>Assigned: {task.assigned_to}</span>}
                            {task.category && <span>Category: {task.category}</span>}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {completedTasks.length > 0 && (
            <div>
              <h2 className="text-lg font-medium mb-4">Completed Tasks ({completedTasks.length})</h2>
              <div className="space-y-3">
                {completedTasks.map((task) => (
                  <Card key={task.id} className="shadow-sm opacity-60">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => toggleTask(task.id, task.completed)}
                          className="mt-1"
                        >
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        </button>
                        <div className="flex-1">
                          <h3 className="font-medium line-through">{task.title}</h3>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-through">{task.description}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};